import {
  AbsoluteFill,
  Sequence,
  Video,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

import React from "react";

export const MyComposition: React.FC<{
  hookVideo?: string;
  productDemo?: string;
  durationInFrames: number;
  overlayText?: string;
  textPosition?: "top" | "center" | "bottom";
  hookDuration?: number;
  productDemoDuration?: number;
}> = ({
  hookVideo,
  productDemo,
  overlayText,
  textPosition = "bottom",
  hookDuration,
  productDemoDuration,
}) => {
  const { width } = useVideoConfig();
  const frame = useCurrentFrame();

  // Create videos array from the separate hook and product demo videos
  const videos = [hookVideo, productDemo].filter(Boolean) as string[];
  const hasVideos = videos.length > 0;

  // Calculate frames for each video based on actual durations
  const hookFrames = hookDuration ? Math.ceil(hookDuration * 30) : 0;
  const productDemoFrames = productDemoDuration
    ? Math.ceil(productDemoDuration * 30)
    : 0;

  // Calculate text position styles based on textPosition prop
  const getTextPositionStyles = () => {
    const baseStyles = {
      position: "absolute" as const,
      left: "50%",
      transform: "translateX(-50%)",
      width: "90%",
      textAlign: "center" as const,
      fontSize: Math.round(width * 0.09),
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: "800",
      color: "white",
      lineHeight: "1.2",
      textShadow: "0 0 8px rgba(0,0,0,0.8), 2px 2px 4px rgba(0,0,0,0.9)",
      letterSpacing: "0.5px",
    };

    switch (textPosition) {
      case "top":
        return { ...baseStyles, top: "10%" };
      case "center":
        return {
          ...baseStyles,
          top: "50%",
          transform: "translate(-50%, -50%)",
        };
      case "bottom":
      default:
        return { ...baseStyles, bottom: "20%" };
    }
  };

  return (
    <AbsoluteFill style={{ backgroundColor: "black" }}>
      {hasVideos ? (
        <>
          {/* Render hook video first */}
          {hookVideo && hookFrames > 0 && (
            <Sequence from={0} durationInFrames={hookFrames}>
              <Video
                src={hookVideo}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Sequence>
          )}

          {/* Render product demo after hook video */}
          {productDemo && productDemoFrames > 0 && (
            <Sequence from={hookFrames} durationInFrames={productDemoFrames}>
              <Video
                src={productDemo}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Sequence>
          )}

          {/* Optional text overlay on hook video only - TikTok style */}
          {overlayText && hookVideo && hookFrames > 0 && frame < hookFrames && (
            <Sequence from={0} durationInFrames={hookFrames}>
              <div style={getTextPositionStyles()}>{overlayText}</div>
            </Sequence>
          )}
        </>
      ) : (
        /* Show placeholder when no videos are available */
        <AbsoluteFill
          style={{
            backgroundColor: "#1a1a1a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              color: "white",
              fontSize: Math.round(width * 0.06),
              fontFamily: "Arial, sans-serif",
              textAlign: "center",
            }}
          >
            Select videos to preview
          </div>
        </AbsoluteFill>
      )}
    </AbsoluteFill>
  );
};
